import type { ReminderPreviewEditField, ReminderPreviewPayload } from '../types/chat'
import { chatContentString } from './chatContent'

/** Parse display or picker time into ``HH:MM`` for API + ``<input type="time">``. */
export function parseReminderTimeToHm(raw: string): string | null {
  const t = (raw || '').trim()
  if (!t) return null
  const m24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m24) {
    const h = Number(m24[1])
    const mi = Number(m24[2])
    if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) {
      return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
    }
  }
  const m12 = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i)
  if (m12) {
    let h = Number(m12[1]) % 12
    if (m12[3].toLowerCase() === 'pm') h += 12
    const mi = m12[2] != null ? Number(m12[2]) : 0
    if (mi >= 0 && mi <= 59) {
      return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
    }
  }
  return null
}

/** Normalize inline Save payload for ``update_preview_field``. */
export function normalizeReminderPreviewFieldValue(
  field: ReminderPreviewEditField,
  draft: string,
): string | null {
  const raw = (draft || '').trim()
  if (!raw) return null
  if (field === 'purpose') return raw
  if (field === 'day') {
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null
  }
  return parseReminderTimeToHm(raw)
}

function reminderPreviewLanguageIsFrench(language?: string): boolean {
  const tag = (language || '').trim().toLowerCase()
  return tag === 'french' || tag === 'fr'
}

function formatReminderInlineEditDayValue(dayIso: string, fr: boolean): string | null {
  const m = dayIso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0))
  return new Intl.DateTimeFormat(fr ? 'fr-FR' : 'en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(dt)
}

function formatReminderInlineEditTimeValue(timeHm: string, fr: boolean): string | null {
  const m = timeHm.match(/^(\d{2}):(\d{2})$/)
  if (!m) return null
  const h = Number(m[1])
  const mi = Number(m[2])
  if (h < 0 || h > 23 || mi < 0 || mi > 59) return null
  if (fr) return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`
  const suffix = h >= 12 ? 'PM' : 'AM'
  const hour12 = h % 12 || 12
  return `${hour12}:${String(mi).padStart(2, '0')} ${suffix}`
}

export function buildReminderInlineEditCommand(
  field: ReminderPreviewEditField,
  draft: string,
  opts?: { language?: string },
): { command: string; displayText: string } | null {
  const fr = reminderPreviewLanguageIsFrench(opts?.language)
  const normalized = normalizeReminderPreviewFieldValue(field, draft)
  if (!normalized) return null

  if (field === 'purpose') {
    const value = draft.trim()
    if (!value) return null
    return {
      command: fr ? `modifier l'objet ${value}` : `edit purpose ${value}`,
      displayText: fr ? `Modifier l'objet: ${value}` : `Edit purpose: ${value}`,
    }
  }

  if (field === 'day') {
    return {
      command: fr ? `modifier le jour ${normalized} UTC` : `edit day ${normalized} UTC`,
      displayText: fr ? `Modifier jour: ${normalized} UTC` : `Edit day: ${normalized} UTC`,
    }
  }

  return {
    command: fr ? `modifier l'heure ${normalized} UTC` : `edit time ${normalized} UTC`,
    displayText: fr ? `Modifier heure: ${normalized} UTC` : `Edit time: ${normalized} UTC`,
  }
}

export function reminderInlineEditDraftForField(
  preview: ReminderPreviewPayload,
  field: ReminderPreviewEditField,
): string {
  if (field === 'purpose') return (preview.purpose || '').trim()
  if (field === 'day') {
    const iso = (preview.dayIso || '').trim()
    if (iso) return iso
    const dt = (preview.datetimeUtc || '').trim()
    if (dt.length >= 10) return dt.slice(0, 10)
    return ''
  }
  if (field === 'time') {
    const hm = (preview.timeHm || '').trim()
    if (hm) return parseReminderTimeToHm(hm) ?? hm
    return parseReminderTimeToHm(preview.time || '') ?? ''
  }
  return ''
}

export function wireRemindPreviewPayload(raw: Record<string, unknown>): ReminderPreviewPayload {
  const dayIso =
    raw.day_iso != null
      ? String(raw.day_iso)
      : raw.dayIso != null
        ? String(raw.dayIso)
        : undefined
  const timeHmRaw =
    raw.time_hm != null
      ? String(raw.time_hm)
      : raw.timeHm != null
        ? String(raw.timeHm)
        : undefined
  const timeHm = timeHmRaw ? parseReminderTimeToHm(timeHmRaw) ?? timeHmRaw : undefined
  const timeDisplay = raw.time != null ? String(raw.time) : ''
  return {
    purpose: raw.purpose != null ? String(raw.purpose) : '',
    day: raw.day != null ? String(raw.day) : '',
    time: timeDisplay,
    language: raw.language != null ? String(raw.language) : undefined,
    datetimeUtc:
      raw.datetime_utc != null
        ? String(raw.datetime_utc)
        : raw.datetimeUtc != null
          ? String(raw.datetimeUtc)
          : undefined,
    dayIso: dayIso || undefined,
    timeHm: timeHm || parseReminderTimeToHm(timeDisplay) || undefined,
  }
}

export function remindPreviewToWireMeta(preview: ReminderPreviewPayload): Record<string, string> {
  const out: Record<string, string> = {
    purpose: preview.purpose || '',
    day: preview.day || '',
    time: preview.time || '',
  }
  if (preview.language) out.language = preview.language
  if (preview.datetimeUtc) out.datetime_utc = preview.datetimeUtc
  if (preview.dayIso) out.day_iso = preview.dayIso
  const hm = preview.timeHm ? parseReminderTimeToHm(preview.timeHm) : parseReminderTimeToHm(preview.time || '')
  if (hm) out.time_hm = hm
  return out
}

/** Parse purpose/day/time lines from a reminder success assistant message. */
export function parseReminderSuccessDetailLines(content: unknown): Partial<ReminderPreviewPayload> {
  const fields: Partial<ReminderPreviewPayload> = {}
  for (const line of chatContentString(content)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)) {
    const purpose = line.match(/^(?:purpose|objet)\s*:+\s*(.+)$/i)
    if (purpose) {
      fields.purpose = purpose[1].trim()
      continue
    }
    const day = line.match(/^(?:day|jour)\s*:+\s*(.+)$/i)
    if (day) {
      fields.day = day[1].trim()
      continue
    }
    const time = line.match(/^(?:time|heure)\s*:+\s*(.+)$/i)
    if (time) {
      fields.time = time[1].trim()
    }
  }
  return fields
}

/** Structured preview for the success card (metadata or parsed message body). */
export function reminderSuccessPreviewFromMessage(input: {
  content?: unknown
  reminderPreview?: ReminderPreviewPayload
}): ReminderPreviewPayload | null {
  const rp = input.reminderPreview
  if (rp && ((rp.purpose || '').trim() || (rp.day || '').trim() || (rp.time || '').trim())) {
    return rp
  }
  const parsed = parseReminderSuccessDetailLines(input.content)
  if (!(parsed.purpose || parsed.day || parsed.time)) return null
  return {
    purpose: (parsed.purpose || '').trim(),
    day: (parsed.day || '').trim(),
    time: (parsed.time || '').trim(),
    language: parsed.language,
  }
}

export function reminderPreviewFromAssistantText(content: unknown): ReminderPreviewPayload | null {
  const parsed = parseReminderSuccessDetailLines(content)
  if (!(parsed.purpose || parsed.day || parsed.time)) return null
  return {
    purpose: (parsed.purpose || '').trim(),
    day: (parsed.day || '').trim(),
    time: (parsed.time || '').trim(),
    language: parsed.language,
  }
}

export function reminderAssistantStateFromText(content: unknown): {
  awaitingConfirmation: boolean
  confirmed: boolean
  cancelled: boolean
} {
  const text = chatContentString(content).trim()
  const low = text.toLowerCase()
  const awaiting =
    low.startsWith('please review the reminder details below.') ||
    low.startsWith('verifiez les details du rappel ci dessous.') ||
    low.startsWith('vérifiez les détails du rappel ci-dessous.')
  const confirmed =
    low.startsWith('reminder set successfully.') || low.startsWith('rappel programme avec succes') || low.startsWith('rappel programmé avec succès')
  const cancelled =
    low.startsWith('the reminder has been cancelled.') ||
    low.startsWith('le rappel a ete annule') ||
    low.startsWith('le rappel a été annulé')
  return { awaitingConfirmation: awaiting, confirmed, cancelled }
}
